import { TourConfig } from '../models/tour-config.model';
import { TourFeature } from '../config/tour-feature.enum';
import { Audience } from '../config/audience.enum';
import { TourTrigger } from '../config/trigger.enum';
import { AnchorId } from '../config/anchor.registry';
import { anchorMissing, ensureSectionExpanded } from './tasks-help.shared';

/**
 * Begruessung in zwei Fassungen (08.10.2026): mit Umschalter „Einzelne Aufgabe | Auftrag mit
 * Schritten“ (Schalter „Auftraege nutzen“, nur beim Anlegen) nennt sie beide Wege, ohne ihn
 * nur die Einzelaufgabe — sonst spraeche sie von einem Umschalter, den niemand sieht (E-05).
 */
export const SHEET_WELCOME_VARIANTS = {
  base: { suffix: '', audioStepId: 'sheet-welcome-v2' },
  kind: { suffix: '_KIND', audioStepId: 'sheet-welcome-kind' },
} as const;

function welcomeVariant() {
  return anchorMissing(AnchorId.TaskSheetKind)
    ? SHEET_WELCOME_VARIANTS.base
    : SHEET_WELCOME_VARIANTS.kind;
}

/**
 * Die Stationen der Einzelaufgabe gibt es im Auftragsmodus nicht. Das ? waehlt dort zwar die
 * Auftrags-Tour (`task-project-sheet-help`), wer aber waehrend der Tour umschaltet, soll nicht
 * 8 s an einem fehlenden Anker haengen.
 */
const missing = (anchor: AnchorId) => () => anchorMissing(anchor);

/**
 * ?-Hilfe fuer den Anlegen-/Bearbeiten-Dialog einer Aufgabe.
 *
 * Traegt drei der sechs fachlichen Kernaussagen des Moduls:
 *
 *  1. **Zustaendig ist nicht dasselbe wie sichtbar.** "Zuweisen an" legt die
 *     Verantwortung fest; wer die Aufgabe nur sehen kann, steht live in der
 *     Fussleiste (`POST /Tasks/visibility-preview`).
 *     Beleg: `task-create-edit-sheet.component.ts` (Fussleisten-Vorschau) und
 *     `modules/tasks/README.md` ("Sichtbarkeit ≠ Verantwortung").
 *  2. **Drei Verteilungsarten.** Einzel / Topf (eine aus der Gruppe uebernimmt)
 *     / Je Person (eine eigene Aufgabe pro Mitglied). Das Feld erscheint nur,
 *     wenn das gewaehlte Ziel mehr als eine Art erlaubt (`AllowedAssignmentModes`).
 *  3. **"Abnahme erforderlich" ist nicht nachtraeglich umstellbar** und schiebt
 *     die Aufgabe nach dem Erledigen auf "Wartet auf Abnahme"
 *     (`*ngIf="!isEdit"` am Schalter).
 *
 * Alle Schritte laufen mit `popoverOutsideDialog`: der Dialog ist `size="lg"`
 * (~940px), ein Popover INNERHALB verdeckt genau das Feld, das es erklaert.
 * `panelIndex: -1` ueberall — der Dialog ist kein Material-Accordion; ohne den
 * Wert liefe der Coordinator in den Legacy-Zweig und klickte jeden Kopf an.
 *
 * Die beiden einklappbaren Abschnitte (Checkliste, Wiederkehr) brauchen
 * {@link ensureSectionExpanded}: `xds-section` versteckt den Body per
 * `[hidden]`, der Anker bleibt also im DOM und `skipIf` wuerde NICHT greifen —
 * der Schritt haenge stattdessen 8s im Sichtbarkeits-Guard.
 */
const dialogStep = {
  attachToDialog: true,
  autoCloseOnDialogClose: true,
  popoverOutsideDialog: true,
} as const;

export const TASK_SHEET_HELP_TOUR: TourConfig = {
  id: TourFeature.TaskSheetHelp,
  version: 1,
  title: 'TASK_SHEET_HELP_TOUR_TITLE',
  description: 'TASK_SHEET_HELP_TOUR_DESC',
  audience: [Audience.Admin, Audience.TeamLeader],
  triggers: [TourTrigger.Manual],
  // Start aus dem Hilfe-Hub: zur Uebersicht navigieren, dann "Aufgabe anlegen"
  // klicken. Aus dem bereits offenen Dialog heraus startet der ?-Knopf direkt.
  launchContext: {
    route: '/tasks/overview',
    opens: { openerAnchor: AnchorId.TasksCreateButton, settleMs: 400 },
    mode: 'auto',
  },
  statePolicy: {
    allowReplay: true,
    snoozeEnabled: false,
    cooldownHoursAfterDismiss: 0,
    cooldownHoursAfterSnooze: 0,
    suppressAfterCompletion: false,
  },
  priority: 100,
  estimatedDurationSec: 150,
  category: 'feature-discovery',
  renumberVisibleSteps: true,
  chapters: [
    {
      id: 'overview',
      title: 'TASK_SHEET_HELP_TOUR_TITLE',
      steps: [
        {
          id: 'sheet-welcome',
          anchor: AnchorId.TaskSheetDialog,
          spotlightAnchor: AnchorId.TaskSheetDialog,
          title: 'TUTORIAL.TASK_SHEET.WELCOME.TITLE',
          content: () => `TUTORIAL.TASK_SHEET.WELCOME.CONTENT${welcomeVariant().suffix}`,
          audioStepId: () => welcomeVariant().audioStepId,
          placement: 'right',
          panelIndex: -1,
          dialogContext: {
            ...dialogStep,
            waitForOpen: true,
            outsideDialog: true,
            highlightDialogHeader: true,
          },
          nextCondition: 'manual',
          showNextButton: true,
          showPrevButton: false,
        },
        {
          id: 'sheet-what',
          anchor: AnchorId.TaskSheetSectionWhat,
          spotlightAnchor: AnchorId.TaskSheetSectionWhat,
          title: 'TUTORIAL.TASK_SHEET.WHAT.TITLE',
          content: 'TUTORIAL.TASK_SHEET.WHAT.CONTENT',
          // -v2 (08.10.2026): Beschreibung ist Pflicht und ein Markdown-Editor.
          audioStepId: 'sheet-what-v2',
          placement: 'right',
          panelIndex: -1,
          dialogContext: dialogStep,
          nextCondition: 'manual',
          showNextButton: true,
          optional: true,
          skipIf: missing(AnchorId.TaskSheetSectionWhat),
        },
        {
          // Kern-Step 1: Verantwortung. Cues pulsen "Zuweisen an" und die
          // Fussleisten-Vorschau (Manifest `task-sheet-help/sheet-target`).
          id: 'sheet-target',
          anchor: AnchorId.TaskSheetTarget,
          spotlightAnchor: AnchorId.TaskSheetTarget,
          title: 'TUTORIAL.TASK_SHEET.TARGET.TITLE',
          content: 'TUTORIAL.TASK_SHEET.TARGET.CONTENT',
          // -v2 (08.10.2026): Abwesenheit bis zur Faelligkeit (B12) steht an der Person.
          audioStepId: 'sheet-target-v2',
          placement: 'right',
          panelIndex: -1,
          dialogContext: dialogStep,
          nextCondition: 'manual',
          showNextButton: true,
          optional: true,
          skipIf: missing(AnchorId.TaskSheetTarget),
        },
        {
          // Kern-Step 2: die drei Verteilungsarten. Das Feld fehlt, solange kein
          // Ziel gewaehlt ist ODER das Ziel nur eine Art erlaubt (Einzelperson).
          id: 'sheet-mode',
          anchor: AnchorId.TaskSheetMode,
          spotlightAnchor: AnchorId.TaskSheetMode,
          title: 'TUTORIAL.TASK_SHEET.MODE.TITLE',
          content: 'TUTORIAL.TASK_SHEET.MODE.CONTENT',
          placement: 'right',
          panelIndex: -1,
          dialogContext: dialogStep,
          nextCondition: 'manual',
          showNextButton: true,
          optional: true,
          skipIf: () => anchorMissing(AnchorId.TaskSheetMode),
        },
        {
          // -v2 (05.10.2026): neue Audio-ID, weil sich der Sprechtext geaendert hat - das Segment
          // Am Datum / Mit Uhrzeit gibt es nicht mehr, die Uhrzeit ist optional.
          id: 'sheet-due-v2',
          anchor: AnchorId.TaskSheetDue,
          spotlightAnchor: AnchorId.TaskSheetDue,
          title: 'TUTORIAL.TASK_SHEET.DUE.TITLE',
          content: 'TUTORIAL.TASK_SHEET.DUE.CONTENT',
          audioStepId: 'sheet-due-v3',
          placement: 'right',
          panelIndex: -1,
          dialogContext: dialogStep,
          nextCondition: 'manual',
          showNextButton: true,
          optional: true,
          skipIf: missing(AnchorId.TaskSheetDue),
        },
        {
          // Kern-Step 3: "Abnahme erforderlich". Seit 08.10.2026 der ganze Abschnitt
          // "Weitere Angaben" (Chips Aufwand/Projekt/Label/Wartet auf + Wichtig + Abnahme);
          // bis dahin nur die Schaltergruppe, die Chips erklaerte niemand. Der Schalter
          // "Abnahme" fehlt im Bearbeiten-Modus — der Cue darauf laeuft dann still ins Leere.
          id: 'sheet-more',
          anchor: AnchorId.TaskSheetSectionMore,
          spotlightAnchor: AnchorId.TaskSheetSectionMore,
          title: 'TUTORIAL.TASK_SHEET.MORE.TITLE',
          content: 'TUTORIAL.TASK_SHEET.MORE.CONTENT',
          placement: 'right',
          panelIndex: -1,
          dialogContext: dialogStep,
          nextCondition: 'manual',
          showNextButton: true,
          optional: true,
          skipIf: missing(AnchorId.TaskSheetSectionMore),
        },
        {
          id: 'sheet-checklist',
          anchor: AnchorId.TaskSheetChecklist,
          spotlightAnchor: AnchorId.TaskSheetChecklist,
          title: 'TUTORIAL.TASK_SHEET.CHECKLIST.TITLE',
          content: 'TUTORIAL.TASK_SHEET.CHECKLIST.CONTENT',
          audioStepId: 'sheet-checklist-v2',
          placement: 'right',
          panelIndex: -1,
          dialogContext: dialogStep,
          nextCondition: 'manual',
          showNextButton: true,
          optional: true,
          skipIf: missing(AnchorId.TaskSheetChecklist),
          onBeforeShow: () => ensureSectionExpanded(AnchorId.TaskSheetChecklist),
        },
        {
          // Nur beim Anlegen und nur mit `ManageRoutines` im DOM. Seit 05.10.2026 eine
          // Hinweiszeile (xds-banner) statt eines einklappbaren Abschnitts - es gibt nichts
          // aufzuklappen, deshalb kein ensureSectionExpanded mehr.
          id: 'sheet-recurring',
          anchor: AnchorId.TaskSheetRecurring,
          spotlightAnchor: AnchorId.TaskSheetRecurring,
          title: 'TUTORIAL.TASK_SHEET.RECURRING.TITLE',
          content: 'TUTORIAL.TASK_SHEET.RECURRING.CONTENT',
          audioStepId: 'sheet-recurring-v2',
          placement: 'right',
          panelIndex: -1,
          dialogContext: dialogStep,
          nextCondition: 'manual',
          showNextButton: true,
          optional: true,
          skipIf: () => anchorMissing(AnchorId.TaskSheetRecurring),
        },
        {
          id: 'sheet-visibility',
          anchor: AnchorId.TaskSheetVisibility,
          spotlightAnchor: AnchorId.TaskSheetVisibility,
          title: 'TUTORIAL.TASK_SHEET.VISIBILITY.TITLE',
          content: 'TUTORIAL.TASK_SHEET.VISIBILITY.CONTENT',
          // -v3 (09.10.2026): ohne „links" — am Telefon steht die Zeile ÜBER den Knöpfen.
          audioStepId: 'sheet-visibility-v3',
          placement: 'top',
          align: 'start',
          panelIndex: -1,
          dialogContext: dialogStep,
          nextCondition: 'manual',
          showNextButton: true,
        },
        {
          id: 'sheet-save',
          anchor: AnchorId.TaskSheetSave,
          spotlightAnchor: AnchorId.TaskSheetSave,
          title: 'TUTORIAL.TASK_SHEET.SAVE.TITLE',
          content: 'TUTORIAL.TASK_SHEET.SAVE.CONTENT',
          audioStepId: 'sheet-save-v2',
          placement: 'top',
          align: 'end',
          panelIndex: -1,
          dialogContext: dialogStep,
          nextCondition: 'manual',
          showNextButton: true,
          actions: [
            {
              labelKey: 'TUTORIAL.TASK_SHEET.SAVE.ACTION_CHECKLIST',
              icon: 'fa-light fa-list-check',
              startTour: TourFeature.TaskChecklistHelp,
              variant: 'secondary',
            },
          ],
        },
      ],
    },
  ],
};
